import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listAllClients, createClient, deleteClient, uploadPhotos, deletePhoto,
  clearToken, getToken, CATEGORIES, photoUrl
} from '../../lib/adminApi';
import { Plus, Upload, Trash2, LogOut, X, Loader2 } from 'lucide-react';

const ClientRow = ({ client, onDelete, onUpload, onDeletePhoto, busy }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    onUpload(client.id, Array.from(fileList));
  };

  return (
    <div className="border border-[#e0d6c5] bg-white p-5 md:p-6 space-y-4" data-testid={`admin-client-${client.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-serif text-xl text-[#1a1614]">{client.name}</h4>
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#a67c3d] mt-1">
            {client.category} &middot; {client.photos.length} photo{client.photos.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          onClick={() => window.confirm(`Delete client "${client.name}"?`) && onDelete(client.id)}
          aria-label="Delete client"
          data-testid={`admin-client-delete-${client.id}`}
          className="text-[#8a7f6f] hover:text-red-600 p-1"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
        data-testid={`admin-client-dropzone-${client.id}`}
        className={`cursor-pointer border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? 'border-[#a67c3d] bg-[#f2ebde]' : 'border-[#c9bfae] hover:border-[#a67c3d] bg-[#faf6f0]'
        }`}
      >
        <Upload size={20} className="mx-auto mb-2 text-[#a67c3d]" />
        <p className="text-sm text-[#4a3f36]">
          Drag &amp; drop photos, an entire folder, or <span className="text-[#1a1614] underline">click to select</span>.
        </p>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*"
          // Enables folder upload while still accepting individual files
          webkitdirectory=""
          onChange={e => handleFiles(e.target.files)}
          className="hidden"
        />
        <p className="text-[10px] mt-2 text-[#8a7f6f] tracking-wide">
          Tip: on Chrome/Edge, dropping a folder uploads every image inside it.
        </p>
      </div>

      {/* Photo grid */}
      {client.photos.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {client.photos.map(p => (
            <div key={p.id} className="relative group aspect-square bg-[#1a1614] overflow-hidden">
              <img src={photoUrl(p.url)} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => window.confirm('Delete this photo?') && onDeletePhoto(client.id, p.id)}
                aria-label="Delete photo"
                className="absolute top-1 right-1 w-7 h-7 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-xs text-[#6b5847]">
          <Loader2 size={14} className="animate-spin" /> Uploading&hellip;
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyClientId, setBusyClientId] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAllClients();
      setClients(rows);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        clearToken();
        nav('/admin/login', { replace: true });
        return;
      }
      setError('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) { nav('/admin/login', { replace: true }); return; }
    refresh();
  }, [refresh, nav]);

  const [newForm, setNewForm] = useState({ name: '', category: 'residential' });
  const addClient = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) return;
    try {
      await createClient(newForm.name.trim(), newForm.category);
      setNewForm({ name: '', category: newForm.category });
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create client');
    }
  };

  const removeClient = async (id) => {
    await deleteClient(id);
    refresh();
  };

  const uploadFor = async (clientId, files) => {
    setBusyClientId(clientId);
    setError('');
    try {
      // Filter to image files only (folder uploads may include non-image files)
      const images = files.filter(f => f.type.startsWith('image/'));
      if (images.length === 0) { setError('No image files found in the drop.'); return; }
      await uploadPhotos(clientId, images);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusyClientId(null);
    }
  };

  const removePhoto = async (clientId, photoId) => {
    await deletePhoto(clientId, photoId);
    refresh();
  };

  const logout = () => { clearToken(); nav('/admin/login'); };

  const groupedByCategory = CATEGORIES.map(cat => ({
    ...cat,
    clients: clients.filter(c => c.category === cat.id)
  }));

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#a67c3d]">RCD Admin</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#1a1614]">Retail Clients &amp; Photos</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="text-sm text-[#1a1614] hover:text-[#a67c3d] inline-flex items-center gap-2">
          <LogOut size={16} /> Sign out
        </button>
      </div>

      {/* Add-client form */}
      <form onSubmit={addClient} className="bg-white border border-[#e0d6c5] p-5 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-3">
          <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-1">Client / project name</label>
          <input
            type="text"
            value={newForm.name}
            onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Godrej HQ Fit-out"
            required
            data-testid="admin-new-client-name"
            className="w-full px-3 py-2 border border-[#c9bfae] focus:border-[#1a1614] outline-none bg-white"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs tracking-[0.2em] uppercase text-[#6b5847] mb-1">Category</label>
          <select
            value={newForm.category}
            onChange={e => setNewForm(f => ({ ...f, category: e.target.value }))}
            data-testid="admin-new-client-category"
            className="w-full px-3 py-2 border border-[#c9bfae] focus:border-[#1a1614] outline-none bg-white"
          >
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <button type="submit" data-testid="admin-new-client-submit" className="px-4 py-2 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] inline-flex items-center gap-2">
          <Plus size={16} /> Add
        </button>
      </form>

      {error && <p className="mb-4 text-sm text-red-600" data-testid="admin-error">{error}</p>}

      {loading ? (
        <p className="text-sm text-[#6b5847]">Loading clients&hellip;</p>
      ) : (
        <div className="space-y-10">
          {groupedByCategory.map(group => (
            <section key={group.id}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-serif text-xl md:text-2xl text-[#1a1614]">{group.label}</h2>
                <p className="text-xs text-[#6b5847]">{group.clients.length} client{group.clients.length === 1 ? '' : 's'}</p>
              </div>
              {group.clients.length === 0 ? (
                <div className="border border-dashed border-[#c9bfae] bg-[#faf6f0] p-6 text-center text-sm text-[#6b5847]">
                  No clients yet. Add one above.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {group.clients.map(c => (
                    <ClientRow
                      key={c.id}
                      client={c}
                      onDelete={removeClient}
                      onUpload={uploadFor}
                      onDeletePhoto={removePhoto}
                      busy={busyClientId === c.id}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
